import React, { memo, useState, useEffect } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Modal from '@mui/material/Modal'
import LoadingButton from '@mui/lab/LoadingButton'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import FilledInput from '@mui/material/FilledInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'

import { useSharedState } from '../../context/state.context'
import { userGql } from '../../gql'
import { useImperativeQuery } from '../../utils'
import { isStrongPassword } from '../../utils/password-rule'
import PasswordChecklist from '../PasswordChecklist'

import styles from './styles'

const useStyles = makeStyles(styles)

const Settings = ({
  loading,
  clearData,
  onChangePassword,
  onChangeClearDataFlag
}) => {
  const classes = useStyles()
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')

  const changePassword = () => {
    if (!password || !isStrongPassword(newPassword)) return

    onChangePassword({ password, newPassword })
  }

  useEffect(() => {
    if (!clearData) return

    setPassword('')
    setNewPassword('')

    onChangeClearDataFlag(false)
  }, [clearData])

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
    >
      <Grid item xs={12}>
        <Typography className={classes.typography} variant="h5">
          Settings
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormControl variant="filled" fullWidth>
          <InputLabel htmlFor="old-password">Password</InputLabel>
          <FilledInput
            id="old-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(prev => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl variant="filled" fullWidth>
          <InputLabel htmlFor="new-password">New Password</InputLabel>
          <FilledInput
            id="new-password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowNewPassword(prev => !prev)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        <PasswordChecklist password={newPassword} />
      </Grid>
      <Grid item xs={12}>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={changePassword}
          disabled={!password || !isStrongPassword(newPassword)}
        >
          Change password
        </LoadingButton>
      </Grid>
    </Grid>
  )
}

Settings.displayName = 'Settings'

Settings.propTypes = {
  loading: PropTypes.bool,
  clearData: PropTypes.bool,
  onChangePassword: PropTypes.func,
  onChangeClearDataFlag: PropTypes.func
}

const UserModal = () => {
  const [state, { showMessage, closeUserModal, login: contextLogin }] =
    useSharedState()
  const classes = useStyles()
  const client = useApolloClient()
  const login = useImperativeQuery(userGql.LOGIN_QUERY)
  const [
    changePassword,
    { loading: loadingChangePassword, data: dataChangePassword }
  ] = useMutation(userGql.CHANGE_PASSWORD_MUTATION)
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loadingLogin, setLoadingLogin] = useState(false)
  const [clearChangePasswordData, setClearChangePasswordData] = useState(false)

  const clearData = () => {
    setAccount('')
    setPassword('')
  }

  const successLogin = () => {
    closeUserModal()
    clearData()

    showMessage({
      type: 'success',
      content: 'Logged in'
    })
  }

  const successChangePassword = () => {
    closeUserModal()
    setClearChangePasswordData(true)

    showMessage({
      type: 'success',
      content: 'Password changed'
    })
  }

  const onClickLogin = async () => {
    if (!account || !password) {
      showMessage({
        type: 'warning',
        content: 'Empty fields are not allowed'
      })
    }

    setLoadingLogin(true)

    try {
      const {
        data: {
          login: { accessToken, refreshToken }
        }
      } = await login({
        account,
        password
      })

      // Drop anything cached from a previous account in this window.
      await client.clearStore()
      contextLogin({ accessToken, refreshToken })
      successLogin()
    } catch (err) {
      showMessage({
        type: 'error',
        content: err.message
      })
    }

    setLoadingLogin(false)
  }

  const handleOnChangePassword = async ({ password, newPassword }) => {
    try {
      await changePassword({
        variables: {
          password,
          new_password: newPassword
        }
      })
    } catch (err) {
      showMessage({
        type: 'error',
        content: err.message
      })
    }
  }

  useEffect(() => {
    if (!dataChangePassword) return

    const { changePassword: tokens } = dataChangePassword

    contextLogin(tokens)
    successChangePassword()
  }, [dataChangePassword])

  return (
    <Modal open={state.showUserModal} onClose={closeUserModal}>
      <Box className={classes.modal} sx={{ flexGrow: 1 }}>
        {!state.user ? (
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Grid item xs={12}>
              <Typography className={classes.typography} variant="h5">
                LOGIN
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="filled-basic"
                label="Account"
                variant="filled"
                fullWidth
                value={account}
                onChange={e => setAccount(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl variant="filled" fullWidth>
                <InputLabel htmlFor="filled-adornment-password">
                  Password
                </InputLabel>
                <FilledInput
                  id="filled-adornment-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(prev => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                loading={loadingLogin}
                variant="contained"
                onClick={onClickLogin}
              >
                Login
              </LoadingButton>
            </Grid>
          </Grid>
        ) : (
          <Settings
            loading={loadingChangePassword}
            clearData={clearChangePasswordData}
            onChangePassword={handleOnChangePassword}
            onChangeClearDataFlag={setClearChangePasswordData}
          />
        )}
      </Box>
    </Modal>
  )
}

export default memo(UserModal)
